import { useState } from 'react';

import {
  Button,
  ButtonProps,
  Icon,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@eluve/components';
import { useTranslation } from '@eluve/i18n';

import { useCopyContentElement } from './useCopyContentElement';

interface CopyRichTextButtonProps {
  getContentElement?: () => HTMLElement | null;
  hideContentInTooltip?: boolean;
  markdownContent?: string;
  onCopy?: () => void;
}

export const CopyRichTextButton: React.FC<
  ButtonProps & CopyRichTextButtonProps
> = ({
  getContentElement,
  hideContentInTooltip = false,
  markdownContent,
  onCopy,
  ...buttonProps
}) => {
  const { t } = useTranslation();
  const defaultTooltipContent = t('common.copyToClipboard');
  const [tooltipContent, setTooltipContent] = useState(defaultTooltipContent);
  const [open, setOpen] = useState(false);

  const handleClick = useCopyContentElement({
    markdownContent,
    element: getContentElement?.(),
    onCopied: () => {
      onCopy?.();
      setTooltipContent(t('common.copied'));
      setOpen(true);
      setTimeout(() => setTooltipContent(defaultTooltipContent), 3000);
    },
  });

  return (
    <Tooltip
      delayDuration={0}
      open={open}
      onOpenChange={setOpen}
      disableHoverableContent={hideContentInTooltip}
    >
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          onClick={handleClick}
          {...buttonProps}
          aria-label={defaultTooltipContent}
        >
          <Icon name="Copy" size="auto" />
        </Button>
      </TooltipTrigger>
      <TooltipContent>{tooltipContent}</TooltipContent>
    </Tooltip>
  );
};
